import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Check as CheckIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Android,
  Apple,
  Shield,
  VerifiedUser,
  Block,
} from '@mui/icons-material';

interface Plan {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  period: string;
  storage: string;
  users: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  isEnterprise?: boolean;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '₹0',
    period: 'Forever free',
    storage: '1 GB',
    users: 'Up to 3 users',
    badge: 'FREE',
    features: [
      '1 GB storage per user',
      'Custom domain email',
      'Webmail access',
      'Mobile apps (iOS & Android)',
      'Basic spam protection',
      'Email forwarding',
    ],
  },
  {
    id: 'mail-lite',
    name: 'Mail Lite',
    price: '₹59',
    period: '/user/month',
    storage: '5 GB',
    users: 'Unlimited users',
    features: [
      '5 GB storage per user',
      'Custom domain email',
      'POP/IMAP access',
      'Advanced spam & virus protection',
      'Email aliases',
      'Auto-responders',
      'Email templates',
    ],
  },
  {
    id: 'mail-premium',
    name: 'Mail Premium',
    price: '₹99',
    originalPrice: '₹129',
    period: '/user/month',
    storage: '50 GB',
    users: 'Unlimited users',
    highlighted: true,
    badge: 'POPULAR',
    features: [
      '50 GB storage per user',
      'Everything in Mail Lite',
      'Email backup & restore',
      'Email archival (1 year)',
      'Large attachments (50 MB)',
      'Priority email delivery',
      'Read receipts',
      '24/7 email support',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: '₹199',
    period: '/user/month',
    storage: '100 GB',
    users: 'Unlimited users',
    features: [
      '100 GB storage per user',
      'Everything in Mail Premium',
      'Calendar & Contacts sync',
      'Email retention policies',
      'Admin console',
      'User management',
      'Activity logs & audit',
      'API access',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '₹399',
    originalPrice: '₹489',
    period: '/user/month',
    storage: '500 GB',
    users: 'Unlimited users',
    badge: 'BEST VALUE',
    features: [
      '500 GB storage per user',
      'Everything in Business',
      'Unlimited email archival',
      'Data loss prevention (DLP)',
      'Advanced security controls',
      'Single Sign-On (SSO)',
      'Dedicated IP address',
      'SLA guarantee (99.9%)',
      'Dedicated account manager',
    ],
  },
  {
    id: 'custom',
    name: 'Custom',
    price: '',
    period: '',
    storage: 'Custom',
    users: 'Custom',
    isEnterprise: true,
    features: [
      'Custom storage limits',
      'White-label solution',
      'On-premise deployment',
      'Custom integrations',
      'Dedicated infrastructure',
      'Custom SLA',
      'Training & onboarding',
    ],
  },
];

const PlanSelection: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkSx = {
    fontSize: '0.875rem', fontWeight: 500, color: '#374151', textDecoration: 'none',
    cursor: 'pointer', transition: 'color 0.2s',
    '&:hover': { color: '#dc2626' },
  };

  const handleSelectPlan = (planId: string) => {
    if (planId === 'custom') {
      window.open('mailto:sales@cruvzmail.com?subject=Custom%20Plan%20Inquiry', '_blank');
      return;
    }
    
    const plan = plans.find(p => p.id === planId);
    sessionStorage.setItem('signupData', JSON.stringify({
      plan: planId,
      planName: plan?.name,
      planStorage: plan?.storage,
      selectedPlan: {
        id: planId,
        name: plan?.name || 'Free',
        storage: plan?.storage || '1 GB',
        price: plan?.price || '₹0',
      },
    }));
    navigate('/setup/organization');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Navbar - matching landing page */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => navigate('/login')}>
            <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: 77 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2.5, letterSpacing: '0.02em', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>Where Business Speaks!</Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/enterprise')}>For Business</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/features')}>Features</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/migration')}>Migration</Typography>
            <Typography component="a" sx={{ ...navLinkSx, color: '#dc2626', fontWeight: 600 }}>Plans and Pricing</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/help-center')}>Support</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button onClick={() => navigate('/login')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button onClick={() => navigate('/signup')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.25)',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', transform: 'translateY(-1px)' },
            }}>Get Started Free</Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ flex: 1, py: 5, pt: { xs: '110px', md: '120px' } }}>
        {/* Title Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              color: '#111827',
              fontWeight: 700,
              mb: 2,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            Choose the perfect plan for your business
          </Typography>

          {/* Features Row */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ color: '#dc2626', fontSize: 20 }} />
              <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem' }}>
                Custom domain email
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ color: '#dc2626', fontSize: 20 }} />
              <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem' }}>
                Spam & virus protection
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon sx={{ color: '#dc2626', fontSize: 20 }} />
              <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem' }}>
                99.9% uptime SLA
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon sx={{ color: '#dc2626', fontSize: 20 }} />
              <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem' }}>
                Secure data centers
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: '#e5e7eb', mb: 4 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontFamily: 'Inter, system-ui, sans-serif',
                color: '#6b7280',
                fontSize: '1rem',
              },
              '& .Mui-selected': {
                color: '#111827 !important',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#dc2626',
                height: 3,
              },
            }}
          >
            <Tab label="Email Plans" />
            <Tab label="Add-ons" />
          </Tabs>
        </Box>

        {/* Plans Grid */}
        {activeTab === 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(6, 1fr)' 
              },
              gap: 2,
              maxWidth: 1600,
              mx: 'auto',
            }}
          >
            {plans.map((plan) => (
              <Box
                key={plan.id}
                sx={{
                  border: plan.highlighted ? '2px solid #dc2626' : '1px solid #e5e7eb',
                  borderRadius: 2,
                  backgroundColor: '#ffffff',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 25px -5px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: plan.badge === 'FREE' ? '#22c55e' : plan.badge === 'POPULAR' ? '#dc2626' : '#f59e0b',
                      color: '#ffffff',
                      px: 1.5,
                      py: 0.3,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      borderRadius: 1,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {plan.badge}
                  </Box>
                )}

                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Plan Name */}
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      fontSize: '1rem',
                      mb: 1,
                      textAlign: 'center',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Price */}
                  {plan.isEnterprise ? (
                    <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                      <PhoneIcon sx={{ fontSize: 32, color: '#6b7280', mb: 0.5 }} />
                      <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Contact us
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '0.9rem', color: '#111827', mt: 0.5 }}>₹</Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: '#111827',
                            fontSize: '2rem',
                            lineHeight: 1,
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {plan.price.replace('₹', '')}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {plan.period}
                      </Typography>
                      {plan.originalPrice && (
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', textDecoration: 'line-through' }}>
                          {plan.originalPrice}/user/month
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Storage & Users */}
                  <Box sx={{ 
                    backgroundColor: '#f8fafc', 
                    borderRadius: 1, 
                    p: 1.5, 
                    mb: 2,
                    textAlign: 'center',
                  }}>
                    <Typography sx={{ color: '#dc2626', fontWeight: 700, fontSize: '1rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {plan.storage}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.7rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      storage per user
                    </Typography>
                    <Typography sx={{ color: '#374151', fontSize: '0.75rem', fontWeight: 500, mt: 0.5, fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {plan.users}
                    </Typography>
                  </Box>

                  {/* Features */}
                  <Box sx={{ flex: 1, mb: 2 }}>
                    {plan.features.slice(0, 6).map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
                        <CheckIcon sx={{ color: '#22c55e', fontSize: 14, mt: 0.3 }} />
                        <Typography sx={{ color: '#374151', fontSize: '0.7rem', lineHeight: 1.4, fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                    {plan.features.length > 6 && (
                      <Typography sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 500, mt: 1, fontFamily: 'Inter, system-ui, sans-serif' }}>
                        +{plan.features.length - 6} more features
                      </Typography>
                    )}
                  </Box>

                  {/* Button */}
                  <Button
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    fullWidth
                    onClick={() => handleSelectPlan(plan.id)}
                    sx={{
                      backgroundColor: plan.highlighted ? '#dc2626' : 'transparent',
                      borderColor: '#dc2626',
                      color: plan.highlighted ? '#ffffff' : '#dc2626',
                      '&:hover': { 
                        backgroundColor: plan.highlighted ? '#b91c1c' : '#fef2f2',
                        borderColor: '#dc2626',
                      },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      py: 1,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {plan.isEnterprise ? 'Contact Sales' : plan.id === 'free' ? 'Start Free' : 'Get Started'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Add-ons Tab */}
        {activeTab === 1 && (
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Storage Add-ons */}
            <Typography sx={{ color: '#111827', fontSize: '1.25rem', fontWeight: 600, mb: 3, fontFamily: 'Inter, system-ui, sans-serif' }}>
              Storage Add-ons
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 5 }}>
              {[
                { name: '+10 GB', price: '₹25', desc: 'Extra email storage' },
                { name: '+25 GB', price: '₹50', desc: 'Extra email storage' },
                { name: '+50 GB', price: '₹89', desc: 'Extra email storage' },
                { name: '+100 GB', price: '₹149', desc: 'Extra email storage' },
              ].map((addon) => (
                <Box key={addon.name} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, backgroundColor: '#ffffff', '&:hover': { borderColor: '#dc2626' } }}>
                  <Typography sx={{ fontWeight: 600, color: '#111827', mb: 0.5, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.name}</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#dc2626', fontSize: '1.25rem', fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.price}<Typography component="span" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>/user/mo</Typography></Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 2, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.desc}</Typography>
                  <Button variant="outlined" size="small" fullWidth sx={{ borderColor: '#dc2626', color: '#dc2626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fef2f2', borderColor: '#dc2626' } }}>Add</Button>
                </Box>
              ))}
            </Box>

            {/* Email Features */}
            <Typography sx={{ color: '#111827', fontSize: '1.25rem', fontWeight: 600, mb: 3, fontFamily: 'Inter, system-ui, sans-serif' }}>
              Email Features
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 5 }}>
              {[
                { name: 'Email Archival', price: '₹49', desc: 'Long-term retention' },
                { name: 'Email Backup', price: '₹39', desc: 'Daily backups' },
                { name: 'Large Attachments', price: '₹29', desc: 'Up to 100 MB' },
                { name: 'Email Encryption', price: '₹59', desc: 'End-to-end encryption' },
              ].map((addon) => (
                <Box key={addon.name} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, backgroundColor: '#ffffff', '&:hover': { borderColor: '#dc2626' } }}>
                  <Typography sx={{ fontWeight: 600, color: '#111827', mb: 0.5, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.name}</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#dc2626', fontSize: '1.25rem', fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.price}<Typography component="span" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>/user/mo</Typography></Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 2, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.desc}</Typography>
                  <Button variant="outlined" size="small" fullWidth sx={{ borderColor: '#dc2626', color: '#dc2626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fef2f2', borderColor: '#dc2626' } }}>Add</Button>
                </Box>
              ))}
            </Box>

            {/* Security Add-ons */}
            <Typography sx={{ color: '#111827', fontSize: '1.25rem', fontWeight: 600, mb: 3, fontFamily: 'Inter, system-ui, sans-serif' }}>
              Security & Compliance
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              {[
                { name: 'Advanced Security', price: '₹79', desc: 'Enhanced protection' },
                { name: 'DLP', price: '₹99', desc: 'Data loss prevention' },
                { name: 'eDiscovery', price: '₹129', desc: 'Legal compliance' },
                { name: 'Dedicated IP', price: '₹199', desc: 'Your own IP address' },
              ].map((addon) => (
                <Box key={addon.name} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, backgroundColor: '#ffffff', '&:hover': { borderColor: '#dc2626' } }}>
                  <Typography sx={{ fontWeight: 600, color: '#111827', mb: 0.5, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.name}</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#dc2626', fontSize: '1.25rem', fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.price}<Typography component="span" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>/user/mo</Typography></Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', mb: 2, fontFamily: 'Inter, system-ui, sans-serif' }}>{addon.desc}</Typography>
                  <Button variant="outlined" size="small" fullWidth sx={{ borderColor: '#dc2626', color: '#dc2626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fef2f2', borderColor: '#dc2626' } }}>Add</Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Footer Note */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
            All prices exclude applicable taxes. 15-day free trial on all paid plans.
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#0a0f1a', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3), rgba(59,130,246,0.3), transparent)' },
      }}>
        {/* Top CTA strip */}
        <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(15,23,42,0) 50%, rgba(59,130,246,0.06) 100%)' }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.02em' }}>Ready to upgrade your business email?</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>Join thousands of businesses already using CruvzMail. Free plan available.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button onClick={() => navigate('/signup')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                boxShadow: '0 4px 20px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #ef4444, #dc2626)', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(220,38,38,0.4)' },
                transition: 'all 0.3s',
              }}>Start Free Trial</Button>
              <Button onClick={() => navigate('/contact')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600, textTransform: 'none',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.3s',
              }}>Contact Sales</Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Main footer content */}
        <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 6, lg: 10 } }}>
            <Box sx={{ flex: '0 0 320px', maxWidth: { xs: '100%', lg: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box component="img" src="/Cruvzmail_Footer_logo.png" alt="CruvzMail" sx={{ height: 80, alignSelf: 'flex-start' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2, letterSpacing: '0.02em' }}>Where Business Speaks!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 3 }}>
                Professional email hosting built for modern businesses. Self-hosted, secure, and fully customizable with your brand.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <Button href="/CruvzMail-v1.0.apk" download sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'all 0.25s', display: 'flex', alignItems: 'center', gap: 1,
                }} startIcon={<Android sx={{ fontSize: 20 }} />}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>Download on</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Android</Typography>
                  </Box>
                </Button>
                <Box sx={{ px: 2.5, py: 1.25, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5 }}>
                  <Apple sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>Coming soon</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1.2 }}>iOS</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: '256-bit encryption' },
                  { icon: <VerifiedUser sx={{ fontSize: 14 }} />, text: '99.9% uptime' },
                  { icon: <Block sx={{ fontSize: 14 }} />, text: 'Zero ads' },
                ].map((badge) => (
                  <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& svg': { color: 'rgba(255,255,255,0.25)' } }}>
                    {badge.icon}
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>{badge.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 4, md: 3 } }}>
              {[
                { title: 'Product', links: [
                  { label: 'Features', href: '/features' }, { label: 'Pricing', href: '/signup' },
                  { label: 'Enterprise', href: '/enterprise' }, { label: 'Migration', href: '/migration' },
                  { label: 'Security', href: '/features' }, { label: 'Mobile App', href: '/CruvzMail-v1.0.apk' },
                ]},
                { title: 'Solutions', links: [
                  { label: 'Small Business', href: '/signup' }, { label: 'Enterprise', href: '/enterprise' },
                  { label: 'Startups', href: '/signup' }, { label: 'Education', href: '/signup' },
                  { label: 'Healthcare', href: '/signup' }, { label: 'Non-Profit', href: '/signup' },
                ]},
                { title: 'Company', links: [
                  { label: 'About Us', href: '/about' }, { label: 'Blog', href: '/blog' },
                  { label: 'Careers', href: '/careers' }, { label: 'Contact', href: '/contact' },
                  { label: 'Press Kit', href: '/about' }, { label: 'Partners', href: '/contact' },
                ]},
                { title: 'Resources', links: [
                  { label: 'Help Center', href: '/help-center' }, { label: 'Documentation', href: '/documentation' },
                  { label: 'API Reference', href: '/documentation' }, { label: 'System Status', href: '/status' },
                  { label: 'Community', href: '/community' }, { label: 'Webinars', href: '/blog' },
                ]},
              ].map((col) => (
                <Box key={col.title}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5 }}>{col.title}</Typography>
                  {col.links.map((link) => (
                    <Typography key={link.label} component="a" href={link.href}
                      onClick={(e: React.MouseEvent) => { if (!link.href.endsWith('.apk')) { e.preventDefault(); navigate(link.href); } }}
                      sx={{ display: 'block', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', mb: 1.5, transition: 'all 0.2s', '&:hover': { color: '#fca5a5', transform: 'translateX(2px)' } }}
                    >{link.label}</Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} CruvzMail by Cruvz Online Shopping India Pvt Ltd. All rights reserved.</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
                { label: 'SLA', href: '/terms-of-service' },
                { label: 'Sitemap', href: '/features' },
              ].map((l, i) => (
                <React.Fragment key={l.label}>
                  {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />}
                  <Typography component="a" href={l.href} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(l.href); }} sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>{l.label}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>All systems operational</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlanSelection;
